// @ts-strict-ignore
import React, {
  useContext, useEffect, useState,
} from 'react';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  selectActions,
  selectNodeLineageForAnchorNode,
  selectRuleBuilderActiveSubEditor,
  selectRuleBuilderPanelBoxContainerWidth,
  selectWorkflow,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { RuleBuilderSubEditorSide, RuleBuilderSubEditorType } from 'flow/store/engine/types';
import {
  buildActionOptions,
  buildAllAttributeOptions,
  computeRuleItemLengthLimit,
  getMultiSelectProps,
  getSelectedOption,
  getShouldHideTooltip,
} from './helpers';
import { ActionType } from 'flow/store/engine/constants';
import type { DropdownOption } from 'flow/editor/components/NodeEditorDrawer/types';
import { updateThreshold } from 'flow/store/redux/thunks/thresholdUpdates';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import type { WorkflowStateChange } from 'flow/store/redux/types';
import { isActionOfType } from 'flow/service/actions';
import { UserContext } from '~/dashboard/context';
import { RuleBuilderContext } from '../../context';
import { ThresholdContext } from './context';
import { AGGREGATION_EDIT_OPTION_LABEL, MLA_ATTRIBUTE_EDIT_OPTION_LABEL } from './constants';
import RuleMultiSelect from '../RuleTemplate/RuleMultiSelect';
import { setRuleBuilderActiveSubEditor } from 'flow/store/redux/slice';
import { getIsPreview } from '../../helpers';
import { StyledEditableControlContainer } from '../styles';
import { Box, Text, Tooltip } from '@usealloy/component-library';
import { MLAType } from 'flow/service/mla';
import MLASuperset from './MLASuperset';
import { doesActionSupportTooltip } from 'flow/editor/components/Graph/nodes/BaseNode/helpers';
import {
  getAggregation,
  getAggregationName,
  isAggregationServiceAction,
} from '~/dashboard/views/workflows/service/services';
import { AttributeWidgetOption } from './AttributeWidgetOption';

const LHSWrapper = () => {
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const dispatch = useAppDispatch();
  const {
    keyboardFocus,
    sourceAction,
    editMode,
    ActionHoverTooltip,
  } = useContext(RuleBuilderContext);
  const { populatedThreshold } = useContext(ThresholdContext);
  const actions = useAppSelector(selectActions);
  const { services, universalAttributes, aggregationConfig } = useAppSelector(selectWorkflowProps);
  const nodeLineage = useAppSelector(selectNodeLineageForAnchorNode);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const workflow = useAppSelector(selectWorkflow);
  const { featureFlags } = useContext(UserContext);
  const ruleEditorPanelBoxContainerWidth = useAppSelector(selectRuleBuilderPanelBoxContainerWidth);
  const lengthLimit = computeRuleItemLengthLimit(ruleEditorPanelBoxContainerWidth);

  const { left, publicId } = populatedThreshold;

  const options = buildActionOptions({
    actions,
    sourceAction,
    services,
    nodeLineage,
    lengthLimit,
    thresholdSide: left,
  });

  const selectedOption = getSelectedOption({
    incomingValue: left.action.publicId,
    options,
  });

  const aggregationName = getAggregationName(left);

  const isServiceAction = isActionOfType(left.action, ActionType.SERVICE);

  const attributeOptions = isServiceAction
    ? buildAllAttributeOptions({
      service: left.service,
      workflow,
      currentAttribute: {
        attributeId: left.attributeId,
        attribute: left.attribute,
      },
      aggregationDisplayName: aggregationName,
      universalAttributes,
      enableSelfServiceMatchingUi: featureFlags?.enableSelfServiceMatchingUi,
      aggregationConfig,
    })
    : [];

  const selectedAttributeOption = isServiceAction
    ? getSelectedOption({
      incomingValue: String(left.attributeId),
      options: attributeOptions,
    })
    : null;
  const fallbackAttributeId = attributeOptions[0]?.value;
  const hasSelectedAttributeOption = Boolean(selectedAttributeOption);
  // When the stored attributeId is no longer in the available options (e.g. a matching-type
  // attribute that is now filtered out), auto-reset to the first available option so the
  // dropdown doesn't disappear.
  // TODO(BUILD-4958): absorb these changes somewhere that won't cause invalid workflow configurations
  useEffect(() => {
    if (!isServiceAction || hasSelectedAttributeOption || fallbackAttributeId === undefined) return;
    dispatch(updateThreshold.leftAttribute(publicId, Number(fallbackAttributeId)));
  }, [dispatch, fallbackAttributeId, hasSelectedAttributeOption, isServiceAction, publicId]);

  function handleDispatch(stateChange: WorkflowStateChange) {
    dispatch(createWorkflowHistoryThunk(
      stateChange,
      Boolean(featureFlags?.workflowEditorUndoRedo),
      {
        operation: Operation.Modify,
        uiEffectContext: createUiEffectContext(changeDescription.threshold, {
          publicId,
          actionId: populatedThreshold.actionId,
        }),
      },
    ));
  }

  const handleChange = (option: DropdownOption) => {
    handleDispatch(() => dispatch(
      updateThreshold.leftAction(publicId, option.value),
    ));
  };

  const handleAttributeChange = (option: DropdownOption) => {
    if (option.label === MLA_ATTRIBUTE_EDIT_OPTION_LABEL) {
      dispatch(
        setRuleBuilderActiveSubEditor({
          thresholdId: populatedThreshold.publicId,
          type: RuleBuilderSubEditorType.MLA_EDITOR,
          side: RuleBuilderSubEditorSide.LHS,
        }),
      );
      return;
    }

    if (option.label === AGGREGATION_EDIT_OPTION_LABEL) {
      keyboardFocus.disable();
      dispatch(
        setRuleBuilderActiveSubEditor({
          thresholdId: populatedThreshold.publicId,
          type: RuleBuilderSubEditorType.AGGREGATIONS_EDITOR,
          side: RuleBuilderSubEditorSide.LHS,
        }),
      );
      return;
    }

    handleDispatch(() => dispatch(
      updateThreshold.leftAttribute(publicId, Number(option.value)),
    ));
  };

  const multiSelectProps = getMultiSelectProps({
    action: left.action,
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  });

  const tooltipContent = ActionHoverTooltip
    && doesActionSupportTooltip(left.action) ? (
      <ActionHoverTooltip key={left.action.publicId} action={left.action} />
    ) : null;

  // For aggregation service actions, surface the aggregation's name/description
  // (from ClientQueryDataConfigFilters.display_name/display_description) when the
  // user hovers over the aggregation attribute label (e.g. "Aggregations: Transactions").
  const aggregation = isAggregationServiceAction(left.action) ? getAggregation(left) : undefined;
  const aggregationTooltipContent = aggregation
    && (aggregation.displayName || aggregation.displayDescription) ? (
      <Box direction="column" alignItems="flex-start" gap={1}>
        {aggregation.displayName && <Text weight={700}>{aggregation.displayName}</Text>}
        {aggregation.displayDescription && <Text>{aggregation.displayDescription}</Text>}
      </Box>
    ) : null;

  return (
    <>
      <Tooltip
        align="start"
        content={tooltipContent}
        side="top"
        variant="secondary"
        disabled={(
          !tooltipContent
          || getShouldHideTooltip(selectedOption)
          || isActionMenuOpen
        )}
        delayDuration={0}
      >
        <StyledEditableControlContainer>
          <RuleMultiSelect
            {...multiSelectProps}
            options={options}
            onChange={handleChange}
            selectedOptions={selectedOption}
            noOptionsMessage={() => 'No options'}
            isMulti={false}
            isNaked
            isMenuOpen={setIsActionMenuOpen}
          />
        </StyledEditableControlContainer>
      </Tooltip>
      {isServiceAction && selectedAttributeOption && (
        <Tooltip
          align="start"
          content={aggregationTooltipContent}
          side="top"
          variant="secondary"
          disabled={!aggregationTooltipContent}
          delayDuration={0}
        >
          <StyledEditableControlContainer>
            <RuleMultiSelect
              {...multiSelectProps}
              options={attributeOptions}
              onChange={handleAttributeChange}
              selectedOptions={selectedAttributeOption}
              isMulti={false}
              isNaked
              components={{ Option: AttributeWidgetOption }}
            />
          </StyledEditableControlContainer>
        </Tooltip>
      )}
      <MLASuperset type={MLAType.THRESHOLD_LEFT} />
    </>
  );
};

export default LHSWrapper;
